import {
  citiesFiltersConverter,
  exportCsvConverter,
  hostOptionsConverter,
  locationHostFilterDataConverter,
  pagableDataConverter,
} from 'data/convertors/general.converters';
import {
  workshopChildcareEditConverter,
  workshopFoodProvidedEditConverter,
  addWorkshopPhotoConverter,
  workshopCreateConverter,
  workshopDetailsConverter,
  workshopFilterConverter,
  workshopGalleryConverter,
  workshopGetConverter,
  workshopLocationEditConverter,
  workshopShipingAddressConverter,
  workshopLocationAddressConverter,
  workshopSiteCoordinatorEditConverter,
  workshopCoordinatorEditConverter,
  workshopClassCreateConverter,
  workshopClassAgeGroupEditConverter,
  classCapacityEditConverter,
  workshopClassLanguageEditConverter,
  workshopClassSessionDateTimeEditConverter,
  workshopClassSessionFacilitatorEditConverter,
  deleteWorkshopClassConverter,
  workshopDescriptionEditConverter,
  workshopHostEditConverter,
  duplicateConverter,
  workshopCapacityEditConverter,
  workshopMeetingLinkEditConverter,
  workshopChildCareConverter,
  workshopPartDateEditConverter,
  workshopPartTimeEditConverter,
  workshopTimezoneEditConverter,
  workshopsNoteConverter,
  createWorkshopsNoteConverter,
  editWorkshopsNoteConverter,
  deleteWorkshopsNoteConverter,
  workshopPartKitOrdersConverter,
  workshopShipmentLocationEditConverter,
  addWorkshopRegistrationConverter,
} from 'data/convertors/workshops.convertors';
import {
  THostOptionUI,
  TLocationHostFilterDataDB,
  TPageableDataWithContentDB,
  TPageableDataWithContentUI,
  TExportCsvDB,
  TExportCsvUI,
  TTableFilterDataUI,
  TTableFilters,
} from 'data/types/generic.types';
import {
  EWorkshopFilterParams,
  TWorkshopChildcareProvidedEditUI,
  TAddWorkshopPhotoUI,
  TWorkshopDataDB,
  TWorkshopDataUI,
  TExportWorkshopsRequest,
  TWorkshopDetailsDataDB,
  TWorkshopDetailsDataUI,
  TWorkshopFoodProvidedEditUI,
  TWorkshopCreateUI,
  TWorkshopGalleryDB,
  TWorkshopGalleryUI,
  TWorkshopLocationEditUI,
  TWorkshopShipmentLocationEditUI,
  TWorkshopShippingAddressEditUI,
  TWorkshopLocationAddressEditUI,
  TWorkshopSiteCoordinatorEditUI,
  TWorkshopCoordinatorEditUI,
  TWorkshopClassDataUI,
  TWorkshopClassAgeGroupEditUI,
  TClassCapacityEditUI,
  TWorkshopClassDateTimeEditUI,
  TWorkshopClassFacilitatorEditUI,
  TDeleteWorkshopClassUI,
  TWorkshopClassLanguageEditUI,
  TWorkshopDescriptionEditUI,
  TWorkshopHostEditUI,
  TDuplicateWorkshopUI,
  TWorkshopCapacityEditUI,
  TWorkshopMeetingLinkEditUI,
  TWorkshopChildCareUI,
  TWorkshopChildCareDB,
  TUpdateCustomQuestionUi,
  TWorkshopRegistrantsUrl,
  TWorkshopPartDateEditUI,
  TWorkshopPartTimeEditUI,
  TWorkshopTimezoneEditUI,
  TWorkshopsNoteDB,
  TWorkshopsNoteUI,
  TWorkshopPartKitOrdersDB,
  TWorkshopPartKitOrdersUI,
  TWorkshopKitOrderHistoryResponse,
  TAddWorkshopRegistrationUI,
} from 'data/types/workshops.types';
import {
  EWorkshopsUrls,
  editMeetingLinkUrl,
  editSessionDateTimeUrl,
  editPartDateUrl,
  editPartTimeUrl,
  editWorkshopTimezoneUrl,
  workshopPartKitOrdersUrl,
} from 'data/urls/workshops.url';
import { authSplitApi } from 'redux/helpers/slice.helpers';
import { downloadFile } from 'redux/services/fileServices';
import { TColumnFilterItem } from 'components/Base/Table/Table.types';
import { kitManagementApi } from './kitManagement.slice';
import { workshopTasksApi } from './workshopTasks.slice';

export type TWorkshopPartKitNoteUI = {
  noteId: string | null;
  note: string;
};

export type TWorkshopPartKitNotesRequest = {
  workshopId: string;
  partId: string;
  notes: TWorkshopPartKitNoteUI[];
};

export type TWorkshopPartKitNotesResponse = {
  noteId: string;
  note: string;
  createdAt: string;
};

const baseUrl = `${EWorkshopsUrls.WORKSHOPS}`;
interface GetWorkshopPartKitOrdersParams {
  workshopId: string;
  sortField?: string;
  sortDirection?: string;
  kitIds?: string;
  workshopPart?: string;
  pageSize?: number;
  page?: number;
}

export const workshopsApi = authSplitApi('workshops', [
  'workshops',
  'workshop',
  'workshop-hosts',
  'workshop-cities',
  'workshop-gallery',
  'workshop-tags',
  'workshop-location',
  'workshops-childCare-count',
  'workshop-part-kit-orders',
  'workshop-part-kit-notes',
]).injectEndpoints({
  endpoints: build => ({
    createWorkshop: build.mutation<string, TWorkshopCreateUI>({
      query(body) {
        return {
          url: `${baseUrl}`,
          method: 'POST',
          body: workshopCreateConverter.toDb(body),
        };
      },
      invalidatesTags: [
        'workshops',
        'workshop-hosts',
        'workshop-cities',
        'workshop-location',
      ],
    }),
    createWorkshopClass: build.mutation<string, TWorkshopClassDataUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CLASS}`,
          method: 'POST',
          body: workshopClassCreateConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    deleteWorkshopClass: build.mutation<void, TDeleteWorkshopClassUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CLASS}`,
          method: 'DELETE',
          body: deleteWorkshopClassConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    // here
    getWorkshops: build.query<
      TPageableDataWithContentUI<TWorkshopDataUI[]>,
      TTableFilters<EWorkshopFilterParams>
    >({
      query({
        page,
        pageSize,
        sortColumn: { sortDirection, sortField },
        filters,
      }) {
        return {
          url: baseUrl,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(filters && { ...workshopFilterConverter(filters) }),
          },
        };
      },
      providesTags: ['workshops'],
      transformResponse: (
        data: TPageableDataWithContentDB<TWorkshopDataDB[]>,
      ) => {
        return {
          ...pagableDataConverter.fromDb(data),
          content: (data.content || []).map(workshop =>
            workshopGetConverter.fromDb(workshop),
          ),
        };
      },
    }),
    exportWorkshops: build.mutation<TExportCsvUI, TExportWorkshopsRequest>({
      query: body => ({
        url: `${baseUrl}/export`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    getWorkshopById: build.query<TWorkshopDetailsDataUI, { id: string }>({
      query({ id }) {
        return {
          url: `${baseUrl}/${id}`,
          method: 'GET',
        };
      },
      providesTags: ['workshop'],
      transformResponse: (data: TWorkshopDetailsDataDB) => {
        return workshopDetailsConverter.fromDb(data || {});
      },
    }),
    getWorkshopChildCareCountById: build.query<
      TWorkshopChildCareUI[],
      { workshopId: string }
    >({
      query({ workshopId }) {
        return {
          url: `${baseUrl}/${workshopId}/${EWorkshopsUrls.CHILDCARE_COUNT}`,
          method: 'GET',
        };
      },
      providesTags: ['workshops-childCare-count'],
      transformResponse: (data: TWorkshopChildCareDB[]) => {
        return data?.length
          ? data.map(item => workshopChildCareConverter.fromDb(item))
          : [];
      },
    }),
    duplicateWorkshop: build.mutation<string, TDuplicateWorkshopUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.DUPLICATE}`,
          method: 'POST',
          body: duplicateConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops'],
    }),
    getWorkshopUrl: build.query<string, { workshopId: string }>({
      query({ workshopId }) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.URL}`,
          method: 'GET',
          params: {
            workshopId,
          },
        };
      },
      transformResponse: (url: string) => {
        try {
          const parsed = new URL(url);
          const id =
            parsed.searchParams.get('action') === 'showWorkshopEvent'
              ? parsed.searchParams.get('id')
              : null;
          if (id) {
            return `${parsed.origin}/workshops/${id}`;
          }
        } catch {
          // fall through to legacy path-based rewrite
        }
        return url.replace(/\/workshop\/([^/?#]+)/, '/workshops/$1');
      },
    }),
    getChildCareUrl: build.query<string, { workshopId: string }>({
      query({ workshopId }) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.REGISTRANTS}/${EWorkshopsUrls.CHILDCARE}/${EWorkshopsUrls.URL}`,
          method: 'GET',
          params: {
            workshopId,
          },
        };
      },
    }),
    getWorkshopRegistrantsUrl: build.query<string, TWorkshopRegistrantsUrl>({
      query(params) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.REGISTRANTS}/${EWorkshopsUrls.URL}`,
          method: 'GET',
          params,
        };
      },
    }),
    getWorkshopPhotoGallery: build.query<TWorkshopGalleryUI, void>({
      query() {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.PHOTO}/${EWorkshopsUrls.GALLERY}`,
          method: 'GET',
        };
      },
      providesTags: ['workshop-gallery'],
      transformResponse: (data: TWorkshopGalleryDB) => {
        return workshopGalleryConverter.fromDb(data || {});
      },
    }),
    addWorkshopPhoto: build.mutation<string, TAddWorkshopPhotoUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.PHOTO}`,
          method: 'PATCH',
          body: addWorkshopPhotoConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshop', 'workshops'],
    }),
    getWorkshopCities: build.query<TColumnFilterItem[], void>({
      query() {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.LOCATION}/${EWorkshopsUrls.CITIES}`,
          method: 'GET',
        };
      },
      transformResponse: (data: string[]) =>
        citiesFiltersConverter.fromDb(data),
      providesTags: ['workshop-cities'],
      keepUnusedDataFor: 3600,
    }),
    getWorkshopHosts: build.query<TTableFilterDataUI[], void>({
      query() {
        return {
          url: `${baseUrl}/hosts-and-elcs`,
          method: 'GET',
        };
      },
      transformResponse: (data: { hosts: TLocationHostFilterDataDB[] }) => {
        return locationHostFilterDataConverter.fromDb(data.hosts);
      },
      providesTags: ['workshop-hosts'],
      keepUnusedDataFor: 3600,
    }),
    getWorkshopHostOptions: build.query<THostOptionUI[], void>({
      query() {
        return {
          url: `${baseUrl}/hosts-and-elcs`,
          method: 'GET',
        };
      },
      transformResponse: (data: { hosts: TLocationHostFilterDataDB[] }) => {
        return hostOptionsConverter.fromDb(data.hosts);
      },
      providesTags: ['workshop-hosts'],
      keepUnusedDataFor: 3600,
    }),
    editAgeGroup: build.mutation<void, TWorkshopClassAgeGroupEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CLASS}/${EWorkshopsUrls.AGE_GROUP}`,
          method: 'PATCH',
          body: workshopClassAgeGroupEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editCapacity: build.mutation<void, TClassCapacityEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CLASS}/${EWorkshopsUrls.CAPACITY}`,
          method: 'PATCH',
          body: classCapacityEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editLanguage: build.mutation<void, TWorkshopClassLanguageEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CLASS}/${EWorkshopsUrls.LANGUAGE}`,
          method: 'PATCH',
          body: workshopClassLanguageEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editSessionDateTime: build.mutation<void, TWorkshopClassDateTimeEditUI>({
      query(body) {
        return {
          url: editSessionDateTimeUrl,
          method: 'PATCH',
          body: workshopClassSessionDateTimeEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-workshops',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editPartDate: build.mutation<void, TWorkshopPartDateEditUI>({
      query(body) {
        return {
          url: `${editPartDateUrl}`,
          method: 'PATCH',
          body: workshopPartDateEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-workshops',
              ]),
            );
            // WorkshopAggregate.updatePartDateTime recomputes the due dates of
            // every task anchored to a part (GROW-4191), so the Tasks tab cache
            // is stale the moment a part date changes. The new dates are derived
            // from each task's dueDateType/dueDays server-side, so refetch
            // rather than trying to mirror that arithmetic here.
            dispatch(
              workshopTasksApi.util.invalidateTags([
                { type: 'workshop-tasks', id: arg.workshopId },
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editPartTime: build.mutation<void, TWorkshopPartTimeEditUI>({
      query(body) {
        return {
          url: `${editPartTimeUrl}`,
          method: 'PATCH',
          body: workshopPartTimeEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-workshops',
              ]),
            );
            // WorkshopAggregate.updatePartDateTime recomputes the due dates of
            // every task anchored to a part (GROW-4191), so the Tasks tab cache
            // is stale the moment a part date changes. The new dates are derived
            // from each task's dueDateType/dueDays server-side, so refetch
            // rather than trying to mirror that arithmetic here.
            dispatch(
              workshopTasksApi.util.invalidateTags([
                { type: 'workshop-tasks', id: arg.workshopId },
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editWorkshopTimezone: build.mutation<void, TWorkshopTimezoneEditUI>({
      query(body) {
        return {
          url: editWorkshopTimezoneUrl,
          method: 'PATCH',
          body: workshopTimezoneEditConverter.toDb(body),
        };
      },
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Optimistically flip the timezone in the cached workshop detail so
        // the selector reflects the click immediately, without waiting for
        // the tag-invalidation refetch to round-trip.
        const patch = dispatch(
          workshopsApi.util.updateQueryData(
            'getWorkshopById',
            { id: arg.workshopId },
            draft => {
              draft.timezone = arg.timezone;
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editSessionFacilitator: build.mutation<
      void,
      TWorkshopClassFacilitatorEditUI
    >({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.SESSION}/${EWorkshopsUrls.FACILITATOR}`,
          method: 'PATCH',
          body: workshopClassSessionFacilitatorEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editLocation: build.mutation<void, TWorkshopLocationEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.LOCATION}`,
          method: 'PATCH',
          body: workshopLocationEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop', 'workshop-location'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-workshops-hosts',
                'kit-management-workshops-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editSiteCoordinator: build.mutation<void, TWorkshopSiteCoordinatorEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.SITE_COORDINATOR}`,
          method: 'PATCH',
          body: workshopSiteCoordinatorEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editWorkshopCoordinator: build.mutation<void, TWorkshopCoordinatorEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.COORDINATOR}`,
          method: 'PATCH',
          body: workshopCoordinatorEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editWorkshopCapacity: build.mutation<void, TWorkshopCapacityEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CAPACITY}`,
          method: 'PATCH',
          body: workshopCapacityEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editFoodProvided: build.mutation<void, TWorkshopFoodProvidedEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.FOOD_PROVIDED}`,
          method: 'PATCH',
          body: workshopFoodProvidedEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editCildcareProvided: build.mutation<
      void,
      TWorkshopChildcareProvidedEditUI
    >({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CHILDCARE_PROVIDED}`,
          method: 'PATCH',
          body: workshopChildcareEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editShippingAddress: build.mutation<void, TWorkshopShippingAddressEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.SHIPMENT_ADDRESS}`,
          method: 'PATCH',
          body: workshopShipingAddressConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editLocationAddress: build.mutation<void, TWorkshopLocationAddressEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.LOCATION_ADDRESS}`,
          method: 'PATCH',
          body: workshopLocationAddressConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    editShipmentLocation: build.mutation<void, TWorkshopShipmentLocationEditUI>(
      {
        query(body) {
          return {
            url: `${baseUrl}/${EWorkshopsUrls.SHIPMENT_LOCATION}`,
            method: 'PATCH',
            body: workshopShipmentLocationEditConverter.toDb(body),
          };
        },
        invalidatesTags: ['workshops', 'workshop'],
      },
    ),
    publishAction: build.mutation<void, { workshopId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.PUBLISH}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-workshops',
                'kit-management-workshops-hosts',
                'kit-management-workshops-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    republishAction: build.mutation<void, { workshopId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.REPUBLISH}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-workshops',
                'kit-management-workshops-hosts',
                'kit-management-workshops-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    rescheduleAction: build.mutation<void, { workshopId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.RESCHEDULE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-workshops',
                'kit-management-workshops-hosts',
                'kit-management-workshops-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    cancelAction: build.mutation<void, { workshopId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CANCEL}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-workshops',
                'kit-management-workshops-hosts',
                'kit-management-workshops-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editDescription: build.mutation<void, TWorkshopDescriptionEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.DESCRIPTION}`,
          method: 'PATCH',
          body: workshopDescriptionEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshop'],
    }),
    editHost: build.mutation<void, TWorkshopHostEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.HOST}`,
          method: 'PATCH',
          body: workshopHostEditConverter.toDb(body),
        };
      },
      invalidatesTags: [
        'workshops',
        'workshop',
        'workshop-hosts',
        'workshop-cities',
        'workshop-location',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-workshops',
                'kit-management-workshops-hosts',
                'kit-management-workshops-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editMeetingLink: build.mutation<void, TWorkshopMeetingLinkEditUI>({
      query(body) {
        return {
          url: editMeetingLinkUrl,
          method: 'PATCH',
          body: workshopMeetingLinkEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshop'],
    }),
    deleteAction: build.mutation<void, { workshopId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${body.workshopId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    getWorkshopLocation: build.query<TTableFilterDataUI[], void>({
      query() {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.LOCATIONS}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TLocationHostFilterDataDB[]) =>
        locationHostFilterDataConverter.fromDb(data),
      providesTags: ['workshop-location'],
      keepUnusedDataFor: 3600,
    }),
    getWorkshopLocationOptions: build.query<THostOptionUI[], void>({
      query() {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.LOCATIONS}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TLocationHostFilterDataDB[]) =>
        hostOptionsConverter.fromDb(data),
      providesTags: ['workshop-location'],
      keepUnusedDataFor: 3600,
    }),
    updateCustomQuestion: build.mutation<void, TUpdateCustomQuestionUi>({
      query({ id, question }) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CUSTOM_QUESTION}`,
          method: 'PATCH',
          body: {
            workshopId: id,
            customQuestion: question,
          },
        };
      },
      invalidatesTags: ['workshop'],
    }),
    deleteCustomQuestion: build.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CUSTOM_QUESTION}`,
          method: 'PATCH',
          body: {
            workshopId: id,
            customQuestion: null,
          },
        };
      },
      invalidatesTags: ['workshop'],
    }),
    closeAction: build.mutation<void, { workshopId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.CLOSE}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['workshops', 'workshop'],
    }),
    getSessionAttendanceUrl: build.query<
      string,
      { workshopId: string; sessionId: string }
    >({
      query(params) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.SESSIONS}/${EWorkshopsUrls.REGISTRANTS}/${EWorkshopsUrls.URL}`,
          method: 'GET',
          params,
        };
      },
    }),
    getWorkshopNotes: build.query<TWorkshopsNoteUI[], { workshopId: string }>({
      query({ workshopId }) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.NOTES}`,
          method: 'GET',
          params: { workshopId },
        };
      },
      providesTags: ['workshop-notes'],
      transformResponse: (data: TWorkshopsNoteDB[]) => {
        return data?.length
          ? data.map(item => workshopsNoteConverter.fromDb(item))
          : [];
      },
    }),
    createWorkshopNotes: build.mutation<string, TWorkshopsNoteUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.NOTES}`,
          method: 'POST',
          body: createWorkshopsNoteConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshop-notes'],
    }),
    editWorkshopNote: build.mutation<void, TWorkshopsNoteUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.NOTES}`,
          method: 'PATCH',
          body: editWorkshopsNoteConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshop-notes'],
    }),
    deleteWorkshopNote: build.mutation<void, TWorkshopsNoteUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.NOTES}`,
          method: 'DELETE',
          body: deleteWorkshopsNoteConverter.toDb(body),
        };
      },
      invalidatesTags: ['workshop-notes'],
    }),
    getWorkshopPartKitOrders: build.query<
      TWorkshopPartKitOrdersUI,
      GetWorkshopPartKitOrdersParams
    >({
      query(params) {
        return {
          url: `${workshopPartKitOrdersUrl}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['workshop-part-kit-orders'],
      transformResponse: (data: TWorkshopPartKitOrdersDB) => {
        return workshopPartKitOrdersConverter.fromDb(data);
      },
    }),
    getWorkshopKitOrderHistory: build.query<
      TWorkshopKitOrderHistoryResponse,
      string
    >({
      query(workshopId) {
        return {
          url: `${EWorkshopsUrls.ORDERING}/workshop/${workshopId}/kit-order-history`,
          method: 'GET',
        };
      },
    }),
    getWorkshopPartKitNotes: build.query<
      TWorkshopPartKitNotesResponse[],
      { workshopId: string; partId: string }
    >({
      query({ workshopId, partId }) {
        return {
          url: `${baseUrl}/${workshopId}/part/${partId}/kit/notes`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { workshopId, partId }) => [
        { type: 'workshop-part-kit-notes', id: `${workshopId}-${partId}` },
      ],
    }),
    updateWorkshopPartKitNotes: build.mutation<
      void,
      { workshopId: string; partId: string; notes: TWorkshopPartKitNoteUI[] }
    >({
      query({ workshopId, partId, notes }) {
        return {
          url: `${baseUrl}/${workshopId}/part/${partId}/kit/notes`,
          method: 'PUT',
          body: notes,
        };
      },
      invalidatesTags: (result, error, { workshopId, partId }) => [
        { type: 'workshop-part-kit-notes', id: `${workshopId}-${partId}` },
      ],
    }),
    deleteWorkshopPartKitNote: build.mutation<
      void,
      { workshopId: string; partId: string; noteId: string }
    >({
      query({ workshopId, partId, noteId }) {
        return {
          url: `${baseUrl}/${workshopId}/part/${partId}/kit/notes`,
          method: 'DELETE',
          body: { noteId },
        };
      },
      invalidatesTags: (result, error, { workshopId, partId }) => [
        { type: 'workshop-part-kit-notes', id: `${workshopId}-${partId}` },
      ],
    }),
    exportWorkshopKitOrders: build.mutation<
      TExportCsvUI,
      {
        columns: string[];
        filters: Record<string, unknown>;
        sort?: { field: string; direction: string };
      }
    >({
      query: body => ({
        url: `${workshopPartKitOrdersUrl}/export`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    addWorkshopRegistration: build.mutation<
      unknown,
      TAddWorkshopRegistrationUI
    >({
      query(body) {
        return {
          url: `${baseUrl}/${EWorkshopsUrls.REGISTRATION}`,
          method: 'POST',
          body: addWorkshopRegistrationConverter.toDb(body),
        };
      },
      // Note: cross-slice invalidation of 'workshop-registrants' tags is intentionally
      // omitted here to avoid a circular import (workshopsApi ↔ registrantsApi).
      // As a result, the registrants list on the same tab will not auto-refresh after
      // adding a registration — the user must manually reload the registrants tab.
      // To fix properly, add a listener in registrantsApi (which already imports workshopsApi)
      // once createListenerMiddleware scaffolding is added to the store.
      invalidatesTags: ['workshops', 'workshop'],
    }),
  }),
});

export const {
  useCreateWorkshopMutation,
  useCreateWorkshopClassMutation,
  useGetWorkshopsQuery,
  useExportWorkshopsMutation,
  useGetWorkshopByIdQuery,
  useGetWorkshopHostsQuery,
  useGetWorkshopHostOptionsQuery,
  useGetWorkshopCitiesQuery,
  useGetWorkshopUrlQuery,
  useGetWorkshopRegistrantsUrlQuery,
  useGetChildCareUrlQuery,
  useEditAgeGroupMutation,
  useEditCapacityMutation,
  useDeleteWorkshopClassMutation,
  useEditLanguageMutation,
  useEditSessionDateTimeMutation,
  useEditSessionFacilitatorMutation,
  useEditLocationMutation,
  useEditSiteCoordinatorMutation,
  useEditWorkshopCoordinatorMutation,
  useEditFoodProvidedMutation,
  useEditCildcareProvidedMutation,
  useEditShippingAddressMutation,
  useEditLocationAddressMutation,
  useEditShipmentLocationMutation,
  useGetWorkshopPhotoGalleryQuery,
  useAddWorkshopPhotoMutation,
  usePublishActionMutation,
  useRepublishActionMutation,
  useRescheduleActionMutation,
  useCancelActionMutation,
  useEditDescriptionMutation,
  useEditHostMutation,
  useDeleteActionMutation,
  useDuplicateWorkshopMutation,
  useEditWorkshopCapacityMutation,
  useEditMeetingLinkMutation,
  useGetWorkshopLocationQuery,
  useGetWorkshopLocationOptionsQuery,
  useGetWorkshopChildCareCountByIdQuery,
  useUpdateCustomQuestionMutation,
  useDeleteCustomQuestionMutation,
  useCloseActionMutation,
  useLazyGetSessionAttendanceUrlQuery,
  useEditPartDateMutation,
  useEditPartTimeMutation,
  useEditWorkshopTimezoneMutation,
  useGetWorkshopNotesQuery,
  useCreateWorkshopNotesMutation,
  useEditWorkshopNoteMutation,
  useDeleteWorkshopNoteMutation,
  useGetWorkshopPartKitOrdersQuery,
  useLazyGetWorkshopPartKitOrdersQuery,
  useGetWorkshopKitOrderHistoryQuery,
  useGetWorkshopPartKitNotesQuery,
  useUpdateWorkshopPartKitNotesMutation,
  useDeleteWorkshopPartKitNoteMutation,
  useExportWorkshopKitOrdersMutation,
  useAddWorkshopRegistrationMutation,
} = workshopsApi;
